import { CardTokenisationRequestStatusEnum } from '@trybeapp/sdk'
import {
  useCancelTokenisationRequest,
  useCreateTokenisationRequest,
  useTokenisationRequest,
} from 'api/TerminalTokenisations'
import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from 'ui/components/Button'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import { Spinner } from 'ui/components/Spinner'
import { ModalButtons } from 'ui/Modal'

export const WaitingForPaymentCard = ({
  terminal,
  onPaymentMethodAdded,
  onCancel,
  orderId,
  customerId,
}) => {
  const { t } = useTranslation()
  const clientId = customerId
  const notification = useNotificationContext()
  const { mutateAsync: createTokenisationRequest, isLoading: isStarting } =
    useCreateTokenisationRequest()
  const { mutate: cancelRequest } = useCancelTokenisationRequest()
  const [paymentId, setPaymentId] = useState<string | null>(null)
  const hasNotifiedRef = useRef(false)

  useEffect(() => {
    if (terminal && !paymentId && !isStarting) {
      createTokenisationRequest(
        {
          customer_id: clientId,
          terminal_id: terminal.id,
        },
        {
          onSuccess: (response) => setPaymentId(response.data.id),
          onError: (response: Response) => {
            notification.notifyApiError(response)
            onCancel()
          },
        }
      )
    }
  }, [terminal, paymentId, createTokenisationRequest, clientId, notification, onCancel, isStarting])

  const { data: { data: tokenisationRequest } = {} } = useTokenisationRequest(paymentId || '', {
    enabled: !!paymentId,
  })

  useEffect(() => {
    if (tokenisationRequest?.status && !hasNotifiedRef.current) {
      const status = tokenisationRequest.status as string

      if (status === 'payment_method_stored') {
        notification.notify(
          <Notification
            title={t('clients.client_profile.waiting_for_payment_card.success_notification.title')}
            description={t(
              'clients.client_profile.waiting_for_payment_card.success_notification.description'
            )}
            variant="success"
            autoDismiss
          />
        )
        hasNotifiedRef.current = true
        onPaymentMethodAdded()
      } else if (status === CardTokenisationRequestStatusEnum.Failed) {
        notification.notify(
          <Notification
            title={t('clients.client_profile.waiting.failed_notification.title')}
            description={t(
              'clients.client_profile.waiting_for_payment_card.failed_notification.description'
            )}
            variant="danger"
          />
        )
        hasNotifiedRef.current = true
        onCancel()
      }
    }
  }, [tokenisationRequest, onPaymentMethodAdded, notification, onCancel, t])

  const handleCancelRequest = () => {
    if (paymentId) {
      cancelRequest(paymentId, {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t('clients.client_profile.waiting.cancelled_notification.title')}
              description={t(
                'clients.client_profile.waiting_for_payment_card.cancelled_notification.description'
              )}
              variant="info"
              autoDismiss
            />
          )
        },
      })
    }
    onCancel()
  }

  if (isStarting) {
    return (
      <div className="text-center py-8">
        <Spinner />
        <div className="mt-2 font-medium">
          {t('clients.client_profile.waiting.is_starting.description_line_first')}
        </div>
        <div className="text-sm text-gray-500 mt-2">
          <Trans
            i18nKey="clients.client_profile.waiting.is_starting.description_line_second"
            values={{ terminalNameOrId: terminal.name || terminal.id }}
            components={[<span className="font-semibold" />]}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="text-center py-8">
      <Spinner />
      <div className="mt-2 font-medium">
        {t('clients.client_profile.waiting_for_payment_card.waiting.description_line_first')}
      </div>
      <div className="text-sm text-gray-500 mt-2">
        <Trans
          i18nKey="clients.client_profile.waiting_for_payment_card.waiting.description_line_second"
          values={{ terminalNameOrId: terminal.name || terminal.id }}
          components={[<span className="font-semibold" />]}
        />
      </div>

      <ModalButtons>
        <Button label={t('modal_button.cancel')} variant="primary" onClick={handleCancelRequest} />
      </ModalButtons>
    </div>
  )
}
